import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminUserBonus.css";

/* =========================================================
   COLLECTIONS
========================================================= */

const TSH_COLLECTION = "tsh_transactions";

const USDT_COLLECTION = "usdt_transactions";

const VISITOR_COLLECTION = "visterdeposte";

const USERS_COLLECTION = "users";

/* =========================================================
   CURRENCY
========================================================= */

const TZS_CURRENCY = "TZS";

const TZS_BALANCE_FIELD = "tshBalance";

const USDT_CURRENCY = "USDT";

const USDT_BALANCE_FIELD = "usdtBalance";

/*
  FIXED RATE

  1 USDT = 2,650 TZS
*/

const USDT_TO_TZS_RATE = 2650;

/* =========================================================
   BONUS SETTINGS
========================================================= */

const FIRST_DEPOSIT_MINIMUM_TZS = 10000;

const SECOND_DEPOSIT_MINIMUM_TZS = 300000;

const WEEKLY_BONUS_PERCENT = 5;

/* =========================================================
   VISITOR COUNTRY → CURRENCY
========================================================= */

const countryCurrency = {
  Tanzania: "TZS",
  Kenya: "KES",
  Uganda: "UGX",
  Rwanda: "RWF",
  Burundi: "BIF",
  "DRC - Congo": "USD",
  Zambia: "ZMW",
  Malawi: "MWK",
  Mozambique: "MZN",
  Zimbabwe: "USD",
  "South Sudan": "SSP",
  Botswana: "BWP",
  Madagascar: "MGA",
};

/* =========================================================
   CURRENCY → TZS RATE
========================================================= */

const currencyRates = {
  USDT: USDT_TO_TZS_RATE,

  KES: 21.5,
  UGX: 0.77,
  RWF: 1.9,
  BIF: 0.94,
  USD: 2762.43,
  ZMW: 147.23,
  MWK: 1.382,
  MZN: 43.71,
  SSP: 0.469,
  BWP: 211.95,
  MGA: 0.684,
};

/* =========================================================
   NORMALIZE HELPERS
========================================================= */

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeUsername = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeDepositType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeCurrency = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

/* =========================================================
   TIMESTAMP
========================================================= */

const getTimestampSeconds = (item) => {
  const value =
    item?.createdAt ||
    item?.timestamp ||
    item?.paidAt ||
    item?.updatedAt;

  if (!value) {
    return 0;
  }

  if (typeof value?.seconds === "number") {
    return Number(value.seconds);
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();

    if (
      date instanceof Date &&
      !Number.isNaN(date.getTime())
    ) {
      return Math.floor(
        date.getTime() / 1000
      );
    }
  }

  if (typeof value === "number") {
    return value > 100000000000
      ? Math.floor(value / 1000)
      : Math.floor(value);
  }

  const parsed = new Date(value).getTime();

  if (Number.isNaN(parsed)) {
    return 0;
  }

  return Math.floor(parsed / 1000);
};

/* =========================================================
   WEDNESDAY
========================================================= */

const isWednesday = (timestampSeconds) => {
  if (!timestampSeconds) {
    return false;
  }

  const date = new Date(
    Number(timestampSeconds) * 1000
  );

  return date.getDay() === 3;
};

const getWednesdayKey = (timestampSeconds) => {
  if (!timestampSeconds) {
    return "";
  }

  const date = new Date(
    Number(timestampSeconds) * 1000
  );

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   FORMAT
========================================================= */

const formatMoney = (
  amount,
  currency = "TZS"
) => {
  return `${Number(
    amount || 0
  ).toLocaleString()} ${currency}`;
};

/* =========================================================
   VISITOR CURRENCY
========================================================= */

const getVisitorCurrency = (item) => {
  const directCurrency =
    normalizeCurrency(item?.currency);

  if (directCurrency) {
    return directCurrency;
  }

  const sourceCurrency =
    normalizeCurrency(
      item?.sourceCurrency
    );

  if (sourceCurrency) {
    return sourceCurrency;
  }

  const coin =
    normalizeCurrency(item?.coin);

  if (coin) {
    return coin;
  }

  const country = item?.country;

  return normalizeCurrency(
    countryCurrency[country] || ""
  );
};

/* =========================================================
   BALANCE FIELD
========================================================= */

const getBalanceFieldFromCurrency = (
  currency
) => {
  const normalized =
    normalizeCurrency(currency);

  if (normalized === "TZS") {
    return TZS_BALANCE_FIELD;
  }

  if (normalized === "USDT") {
    return USDT_BALANCE_FIELD;
  }

  if (!normalized) {
    return "";
  }

  return `${normalized}Balance`;
};

/* =========================================================
   TRANSACTION CURRENCY
========================================================= */

const getTransactionCurrency = (tx) => {
  /*
    TSH / Tanzania
  */

  if (
    tx?.bonusCollection ===
      TSH_COLLECTION ||
    tx?.source === TSH_COLLECTION
  ) {
    return TZS_CURRENCY;
  }

  /*
    USDT COLLECTION

    IMPORTANT:
    Anything inside usdt_transactions
    is always treated as USDT.
  */

  if (
    tx?.bonusCollection ===
      USDT_COLLECTION ||
    normalizeCurrency(tx?.coin) ===
      USDT_CURRENCY ||
    normalizeCurrency(tx?.currency) ===
      USDT_CURRENCY
  ) {
    return USDT_CURRENCY;
  }

  /*
    Visitor
  */

  return getVisitorCurrency(tx);
};

/* =========================================================
   CURRENCY RATE
========================================================= */

const getCurrencyRate = (
  currency,
  country
) => {
  const normalizedCurrency =
    normalizeCurrency(currency);

  if (normalizedCurrency === "TZS") {
    return 1;
  }

  /*
    USDT ALWAYS 2650
  */

  if (
    normalizedCurrency ===
    "USDT"
  ) {
    return USDT_TO_TZS_RATE;
  }

  const directRate = Number(
    currencyRates[
      normalizedCurrency
    ] || 0
  );

  if (directRate > 0) {
    return directRate;
  }

  if (country) {
    const countryCurrencyCode =
      normalizeCurrency(
        countryCurrency[country] || ""
      );

    if (countryCurrencyCode) {
      const countryRate = Number(
        currencyRates[
          countryCurrencyCode
        ] || 0
      );

      if (countryRate > 0) {
        return countryRate;
      }
    }
  }

  return 0;
};

/* =========================================================
   ORIGINAL CURRENCY → TZS
========================================================= */

const convertToTZS = (
  amount,
  currency,
  country
) => {
  const numericAmount = Number(
    amount || 0
  );

  if (numericAmount <= 0) {
    return 0;
  }

  const normalizedCurrency =
    normalizeCurrency(currency);

  if (
    normalizedCurrency ===
    "TZS"
  ) {
    return Math.round(
      numericAmount
    );
  }

  /*
    USDT

    10 USDT × 2650
    = 26,500 TZS
  */

  if (
    normalizedCurrency ===
    "USDT"
  ) {
    return Math.round(
      numericAmount *
        USDT_TO_TZS_RATE
    );
  }

  const rate =
    getCurrencyRate(
      normalizedCurrency,
      country
    );

  if (rate <= 0) {
    return 0;
  }

  return Math.round(
    numericAmount * rate
  );
};

/* =========================================================
   TZS → ORIGINAL CURRENCY
========================================================= */

const convertTZSToCurrency = (
  amountTZS,
  currency,
  country
) => {
  const tzsAmount = Number(
    amountTZS || 0
  );

  if (tzsAmount <= 0) {
    return 0;
  }

  const normalizedCurrency =
    normalizeCurrency(currency);

  if (
    normalizedCurrency ===
    "TZS"
  ) {
    return Math.round(
      tzsAmount
    );
  }

  /*
    USDT

    13,250 TZS ÷ 2650
    = 5 USDT
  */

  if (
    normalizedCurrency ===
    "USDT"
  ) {
    return Number(
      (
        tzsAmount /
        USDT_TO_TZS_RATE
      ).toFixed(6)
    );
  }

  const rate =
    getCurrencyRate(
      normalizedCurrency,
      country
    );

  if (rate <= 0) {
    return 0;
  }

  return Number(
    (
      tzsAmount / rate
    ).toFixed(6)
  );
};

/* =========================================================
   FIRST DEPOSIT BONUS
========================================================= */

const getFirstDepositBonusData = (
  originalAmount,
  tzsEquivalent
) => {
  const amount = Number(
    originalAmount || 0
  );

  const tzsValue = Number(
    tzsEquivalent || 0
  );

  if (
    tzsValue <
    FIRST_DEPOSIT_MINIMUM_TZS
  ) {
    return {
      locked: true,
      percent: 0,
      amount: 0,
      amountTZS: 0,
      label: `Locked - Min ${FIRST_DEPOSIT_MINIMUM_TZS.toLocaleString()} TZS`,
      bonusType: "first",
    };
  }

  /*
    10,000 → 999,999
    10%
  */

  if (tzsValue < 1000000) {
    return {
      locked: false,
      percent: 10,

      /*
        Bonus remains
        in original currency.
      */

      amount: Number(
        (
          amount * 0.1
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.1
      ),

      label:
        "First Deposit 10%",

      bonusType: "first",
    };
  }

  /*
    1,000,000 → 2,999,999
    8%
  */

  if (tzsValue < 3000000) {
    return {
      locked: false,
      percent: 8,

      amount: Number(
        (
          amount * 0.08
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.08
      ),

      label:
        "First Deposit 8%",

      bonusType: "first",
    };
  }

  /*
    3,000,000+
    6%
  */

  return {
    locked: false,
    percent: 6,

    amount: Number(
      (
        amount * 0.06
      ).toFixed(6)
    ),

    amountTZS: Math.round(
      tzsValue * 0.06
    ),

    label:
      "First Deposit 6%",

    bonusType: "first",
  };
};

/* =========================================================
   SECOND DEPOSIT BONUS
========================================================= */

const getSecondDepositBonusData = (
  originalAmount,
  tzsEquivalent
) => {
  const amount = Number(
    originalAmount || 0
  );

  const tzsValue = Number(
    tzsEquivalent || 0
  );

  if (
    tzsValue <
    SECOND_DEPOSIT_MINIMUM_TZS
  ) {
    return {
      locked: true,
      percent: 0,
      amount: 0,
      amountTZS: 0,

      label: `Locked - Min ${SECOND_DEPOSIT_MINIMUM_TZS.toLocaleString()} TZS`,

      bonusType: "second",
    };
  }

  if (tzsValue < 500000) {
    return {
      locked: false,
      percent: 5,

      amount: Number(
        (
          amount * 0.05
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.05
      ),

      label:
        "Second Deposit 5%",

      bonusType: "second",
    };
  }

  if (tzsValue < 1000000) {
    return {
      locked: false,
      percent: 6,

      amount: Number(
        (
          amount * 0.06
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.06
      ),

      label:
        "Second Deposit 6%",

      bonusType: "second",
    };
  }

  if (tzsValue < 3000000) {
    return {
      locked: false,
      percent: 7,

      amount: Number(
        (
          amount * 0.07
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.07
      ),

      label:
        "Second Deposit 7%",

      bonusType: "second",
    };
  }

  if (tzsValue < 5000000) {
    return {
      locked: false,
      percent: 8,

      amount: Number(
        (
          amount * 0.08
        ).toFixed(6)
      ),

      amountTZS: Math.round(
        tzsValue * 0.08
      ),

      label:
        "Second Deposit 8%",

      bonusType: "second",
    };
  }

  /*
    Fixed 500,000 TZS
    later converted to
    original deposit currency.
  */

  return {
    locked: false,

    percent: 0,

    amount: 0,

    amountTZS: 500000,

    label:
      "Second Deposit 500,000 TZS Fixed",

    bonusType: "second",

    fixedTZS: true,
  };
};

/* =========================================================
   WEEKLY BONUS
========================================================= */

const getWeeklyBonusData = (
  originalAmount,
  timestampSeconds
) => {
  const amount = Number(
    originalAmount || 0
  );

  if (
    !timestampSeconds ||
    !isWednesday(timestampSeconds)
  ) {
    return {
      eligible: false,
      locked: true,
      percent: 0,
      amount: 0,
      amountTZS: 0,

      label:
        "Weekly Bonus - Wednesday Only",

      bonusType: "weekly",
    };
  }

  const bonusAmount =
    Number(
      (
        amount *
        (
          WEEKLY_BONUS_PERCENT /
          100
        )
      ).toFixed(6)
    );

  if (bonusAmount <= 0) {
    return {
      eligible: false,
      locked: true,
      percent: 0,
      amount: 0,
      amountTZS: 0,

      label:
        "Weekly Bonus - Invalid Amount",

      bonusType: "weekly",
    };
  }

  return {
    eligible: true,
    locked: false,

    percent:
      WEEKLY_BONUS_PERCENT,

    /*
      Original currency
    */

    amount: bonusAmount,

    amountTZS: 0,

    label:
      "Weekly Deposit Bonus 5%",

    bonusType: "weekly",
  };
};

/* =========================================================
   REFERRAL BONUS
========================================================= */

const getReferralBonusData = (
  depositType,
  tzsEquivalent
) => {
  const tzsValue = Number(
    tzsEquivalent || 0
  );

  if (
    depositType !== "first"
  ) {
    return {
      locked: true,
      percent: 0,
      amountTZS: 0,
      label:
        "No Referral Bonus",
    };
  }

  if (
    tzsValue <
    FIRST_DEPOSIT_MINIMUM_TZS
  ) {
    return {
      locked: true,
      percent: 0,
      amountTZS: 0,

      label: `No Referral Bonus - Min ${FIRST_DEPOSIT_MINIMUM_TZS.toLocaleString()} TZS`,
    };
  }

  /*
    10,000 → 99,999
    10%
  */

  if (tzsValue < 100000) {
    return {
      locked: false,
      percent: 10,

      amountTZS: Math.round(
        tzsValue * 0.1
      ),

      label:
        "Referral 10%",
    };
  }

  /*
    100,000 → 299,999
    8%
  */

  if (tzsValue < 300000) {
    return {
      locked: false,
      percent: 8,

      amountTZS: Math.round(
        tzsValue * 0.08
      ),

      label:
        "Referral 8%",
    };
  }

  /*
    300,000 → 499,999
    6%
  */

  if (tzsValue < 500000) {
    return {
      locked: false,
      percent: 6,

      amountTZS: Math.round(
        tzsValue * 0.06
      ),

      label:
        "Referral 6%",
    };
  }

  /*
    500,000 → 999,999
    5%
  */

  if (tzsValue < 1000000) {
    return {
      locked: false,
      percent: 5,

      amountTZS: Math.round(
        tzsValue * 0.05
      ),

      label:
        "Referral 5%",
    };
  }

  /*
    1,000,000+
    4%
  */

  return {
    locked: false,
    percent: 4,

    amountTZS: Math.round(
      tzsValue * 0.04
    ),

    label:
      "Referral 4%",
  };
};

/* =========================================================
   REFERRAL ALREADY PAID
========================================================= */

const isReferralAlreadyPaid = (
  item
) => {
  if (!item) {
    return false;
  }

  if (
    item.referralBonusGiven ===
    true
  ) {
    return true;
  }

  if (
    item.referralBonusClaimed ===
    true
  ) {
    return true;
  }

  if (
    item.referralClaimed ===
    true
  ) {
    return true;
  }

  const status = String(
    item.referralBonusStatus ||
      item.referralStatus ||
      ""
  )
    .trim()
    .toLowerCase();

  if (
    status === "paid" ||
    status === "claimed" ||
    status === "completed"
  ) {
    return true;
  }

  if (
    item.referralBonusClaimedAt
  ) {
    return true;
  }

  if (
    item.referralClaimedAt
  ) {
    return true;
  }

  return false;
};

/* =========================================================
   FIND USER
========================================================= */

const findUserByUsername = async (
  username
) => {
  if (
    !username ||
    normalizeUsername(username) ===
      "unknown"
  ) {
    return null;
  }

  const usersRef =
    collection(
      db,
      USERS_COLLECTION
    );

  try {
    const q = query(
      usersRef,
      where(
        "username",
        "==",
        username
      )
    );

    const snap =
      await getDocs(q);

    if (!snap.empty) {
      return snap.docs[0];
    }
  } catch (error) {
    console.warn(
      "Exact username search failed:",
      error
    );
  }

  /*
    Fallback:
    case-insensitive username
  */

  const allUsers =
    await getDocs(usersRef);

  const normalized =
    normalizeUsername(username);

  return (
    allUsers.docs.find(
      (userDoc) =>
        normalizeUsername(
          userDoc.data()?.username
        ) === normalized
    ) || null
  );
};

/* =========================================================
   GET USER REFERRAL
========================================================= */

const getUserReferralUsername = (
  userData
) => {
  if (!userData) {
    return "";
  }

  const referral = String(
    userData.referralBy ||
      userData.referredBy ||
      userData.referrerUsername ||
      ""
  ).trim();

  if (!referral) {
    return "";
  }

  if (
    normalizeUsername(referral) ===
      "none" ||
    normalizeUsername(referral) ===
      "unknown"
  ) {
    return "";
  }

  return referral;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function AdminBonus() {
  const [
    bonusList,
    setBonusList,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    updatingId,
    setUpdatingId,
  ] = useState(null);

  const [
    searchUser,
    setSearchUser,
  ] = useState("");

  /* =======================================================
     FETCH BONUSES
  ======================================================= */

  const fetchBonuses =
    useCallback(async () => {
      setLoading(true);

      try {
        /* =================================================
           USERS
        ================================================= */

        const usersSnap =
          await getDocs(
            collection(
              db,
              USERS_COLLECTION
            )
          );

        const usersByUsername = {};

        usersSnap.docs.forEach(
          (userDoc) => {
            const userData =
              userDoc.data();

            const username =
              normalizeUsername(
                userData.username
              );

            if (username) {
              usersByUsername[
                username
              ] = {
                id: userDoc.id,
                ...userData,
              };
            }
          }
        );

        /* =================================================
           TZS
        ================================================= */

        const tshSnap =
          await getDocs(
            collection(
              db,
              TSH_COLLECTION
            )
          );

        const tshTransactions =
          tshSnap.docs.map(
            (document) => {
              const data =
                document.data();

              const amount =
                Number(
                  data.amount || 0
                );

              const username =
                data.username ||
                "Unknown";

              const userData =
                usersByUsername[
                  normalizeUsername(
                    username
                  )
                ] || {};

              const referralBy =
                String(
                  data.referralBy ||
                    getUserReferralUsername(
                      userData
                    ) ||
                    ""
                ).trim();

              return {
                id: document.id,
                ...data,

                referralBy,

                bonusCollection:
                  TSH_COLLECTION,

                bonusCurrency:
                  TZS_CURRENCY,

                bonusBalanceField:
                  TZS_BALANCE_FIELD,

                originalAmount:
                  amount,

                amountForBonus:
                  amount,

                tshEquivalent:
                  amount,

                displayTZSValue:
                  amount,

                displayCurrency:
                  TZS_CURRENCY,

                displayAmount:
                  amount,
              };
            }
          );

        /* =================================================
           USDT
        ================================================= */

        const usdtSnap =
          await getDocs(
            collection(
              db,
              USDT_COLLECTION
            )
          );

        const usdtTransactions =
          usdtSnap.docs.map(
            (document) => {
              const data =
                document.data();

              const amount =
                Number(
                  data.amount || 0
                );

              const username =
                data.username ||
                "Unknown";

              const userData =
                usersByUsername[
                  normalizeUsername(
                    username
                  )
                ] || {};

              const referralBy =
                String(
                  data.referralBy ||
                    getUserReferralUsername(
                      userData
                    ) ||
                    ""
                ).trim();

              /*
                USDT is always
                USDT.
              */

              const tzsEquivalent =
                convertToTZS(
                  amount,
                  USDT_CURRENCY,
                  ""
                );

              return {
                id: document.id,
                ...data,

                referralBy,

                bonusCollection:
                  USDT_COLLECTION,

                bonusCurrency:
                  USDT_CURRENCY,

                bonusBalanceField:
                  USDT_BALANCE_FIELD,

                originalAmount:
                  amount,

                amountForBonus:
                  amount,

                tshEquivalent:
                  tzsEquivalent,

                displayTZSValue:
                  tzsEquivalent,

                displayCurrency:
                  USDT_CURRENCY,

                displayAmount:
                  amount,
              };
            }
          );

        /* =================================================
           VISITOR
        ================================================= */

        const visitorSnap =
          await getDocs(
            collection(
              db,
              VISITOR_COLLECTION
            )
          );

        const visitorTransactions =
          visitorSnap.docs.map(
            (document) => {
              const data =
                document.data();

              const currency =
                getVisitorCurrency(
                  data
                );

              const amount =
                Number(
                  data.amount || 0
                );

              const balanceField =
                getBalanceFieldFromCurrency(
                  currency
                );

              const tzsEquivalent =
                convertToTZS(
                  amount,
                  currency,
                  data.country
                );

              const username =
                data.username ||
                "Unknown";

              const userData =
                usersByUsername[
                  normalizeUsername(
                    username
                  )
                ] || {};

              const referralBy =
                String(
                  data.referralBy ||
                    getUserReferralUsername(
                      userData
                    ) ||
                    ""
                ).trim();

              return {
                id: document.id,
                ...data,

                referralBy,

                bonusCollection:
                  VISITOR_COLLECTION,

                bonusCurrency:
                  currency,

                bonusBalanceField:
                  balanceField,

                originalAmount:
                  amount,

                amountForBonus:
                  amount,

                tshEquivalent:
                  tzsEquivalent,

                displayTZSValue:
                  tzsEquivalent,

                displayCurrency:
                  currency,

                displayAmount:
                  amount,
              };
            }
          );

        /* =================================================
           SUCCESS ONLY

           IMPORTANT:
           USDT collection included here.
        ================================================= */

        const allSuccessful = [
          ...tshTransactions,
          ...usdtTransactions,
          ...visitorTransactions,
        ].filter(
          (tx) =>
            normalizeStatus(
              tx.status
            ) === "success"
        );

        /* =================================================
           OLD → NEW
        ================================================= */

        allSuccessful.sort(
          (a, b) =>
            getTimestampSeconds(a) -
            getTimestampSeconds(b)
        );

        const counts = {};

        const prepared = [];

        /* =================================================
           PREPARE
        ================================================= */

        for (
          const tx of
          allSuccessful
        ) {
          const username =
            tx.username ||
            "Unknown";

          const normalized =
            normalizeUsername(
              username
            );

          counts[normalized] =
            (counts[normalized] ||
              0) + 1;

          let depositOrderType =
            normalizeDepositType(
              tx.depositOrderType ||
                tx.position
            );

          if (
            depositOrderType !==
              "first" &&
            depositOrderType !==
              "second" &&
            depositOrderType !==
              "other"
          ) {
            if (
              counts[normalized] ===
              1
            ) {
              depositOrderType =
                "first";
            } else if (
              counts[normalized] ===
              2
            ) {
              depositOrderType =
                "second";
            } else {
              depositOrderType =
                "other";
            }
          }

          /*
            IMPORTANT:
            For USDT collection,
            currency is always USDT.
          */

          const originalAmount =
            Number(
              tx.amountForBonus ||
                tx.amount ||
                0
            );

          const transactionCurrency =
            getTransactionCurrency(
              tx
            );

          const tzsEquivalent =
            tx.bonusCollection ===
              TSH_COLLECTION
              ? originalAmount
              : convertToTZS(
                  originalAmount,
                  transactionCurrency,
                  tx.country
                );

          const timestamp =
            getTimestampSeconds(
              tx
            );


            const isWeeklyDeposit =
            depositOrderType ===
              "other" &&
            isWednesday(
              timestamp
            );

          /* =================================================
             SELF BONUS
          ================================================= */

          let selfBonus = {
            locked: true,
            percent: 0,
            amount: 0,
            amountTZS: 0,

            label:
              "No Bonus",

            bonusType:
              depositOrderType,
          };

          if (
            isWeeklyDeposit
          ) {
            selfBonus =
              getWeeklyBonusData(
                originalAmount,
                timestamp
              );
          } else if (
            depositOrderType ===
            "first"
          ) {
            selfBonus =
              getFirstDepositBonusData(
                originalAmount,
                tzsEquivalent
              );
          } else if (
            depositOrderType ===
            "second"
          ) {
            selfBonus =
              getSecondDepositBonusData(
                originalAmount,
                tzsEquivalent
              );
          }

          /* =================================================
             REFERRAL
          ================================================= */

          const referralBy =
            String(
              tx.referralBy || ""
            ).trim();

          const referral =
            getReferralBonusData(
              depositOrderType,
              tzsEquivalent
            );

          const referralAmountTZS =
            Number(
              tx.referralBonusAmountTZS ??
                referral.amountTZS ??
                0
            );

          /* =================================================
             SAVED BONUS
          ================================================= */

          const savedSelfBonus =
            Number(
              tx.selfBonusAmount ||
                tx.selfBonusClaimedAmount ||
                0
            );

          const calculatedSelfBonus =
            Number(
              selfBonus.amount || 0
            );

          let displaySelfBonus =
            savedSelfBonus;

          if (
            tx.selfBonusGiven !==
            true
          ) {
            if (
              selfBonus.fixedTZS ===
              true
            ) {
              displaySelfBonus =
                convertTZSToCurrency(
                  500000,
                  transactionCurrency,
                  tx.country
                );
            } else {
              displaySelfBonus =
                calculatedSelfBonus;
            }
          }

          prepared.push({
            ...tx,

            uniqueId:
              `${tx.bonusCollection}-${tx.id}`,

            username,

            depositOrderType,

            position:
              depositOrderType,

            originalAmount,

            amountForBonus:
              originalAmount,

            tshEquivalent:
              tzsEquivalent,

            displayTZSValue:
              tzsEquivalent,

            displayCurrency:
              transactionCurrency,

            displayAmount:
              originalAmount,

            timestamp,

            isWeeklyDeposit,

            /* USER BONUS */

            selfBonusLocked:
              tx.selfBonusLocked ??
              selfBonus.locked,

            selfBonusPercent:
              tx.selfBonusPercent ??
              selfBonus.percent,

            selfBonusAmount:
              displaySelfBonus,

            calculatedSelfBonus:
              displaySelfBonus,

            selfBonusAmountTZS:
              tx.selfBonusAmountTZS ??
              selfBonus.amountTZS ??
              null,

            selfBonusLabel:
              tx.selfBonusLabel ??
              selfBonus.label,

            selfBonusType:
              tx.selfBonusType ??
              selfBonus.bonusType ??
              depositOrderType,

            selfBonusGiven:
              tx.selfBonusGiven ===
              true,

            adminBonusGiven:
              tx.adminBonusGiven ===
              true,

            /* REFERRAL */

            referralBy:
              referralBy ||
              "None",

            referralBonusPercent:
              tx.referralBonusPercent ??
              referral.percent,

            referralBonusAmount:
              Number(
                tx.referralBonusClaimedAmount ||
                  tx.referralBonusAmount ||
                  0
              ),

            referralBonusAmountTZS:
              referralAmountTZS,

            referralBonusLabel:
              tx.referralBonusLabel ??
              referral.label,

            referralBonusGiven:
              isReferralAlreadyPaid(
                tx
              ),

            referralBonusClaimed:
              tx.referralBonusClaimed ===
              true,

            referralClaimed:
              tx.referralClaimed ===
              true,

            referralBonusStatus:
              tx.referralBonusStatus ||
              tx.referralStatus ||
              "",

            referralBonusClaimedAt:
              tx.referralBonusClaimedAt ||
              tx.referralClaimedAt ||
              null,

            referralBonusReceiver:
              tx.referralBonusReceiver ||
              "",

            referralBonusCurrency:
              tx.referralBonusCurrency ||
              "",

            referralBonusBalanceField:
              tx.referralBonusBalanceField ||
              "",
          });
        }

        /* =================================================
           NEW → OLD
        ================================================= */

        prepared.sort(
          (a, b) =>
            getTimestampSeconds(b) -
            getTimestampSeconds(a)
        );

        setBonusList(
          prepared
        );
      } catch (error) {
        console.error(
          "Fetch bonuses failed:",
          error
        );

        alert(
          error?.message ||
            "Failed to load bonuses."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchBonuses();
  }, [fetchBonuses]);

  /* =======================================================
     APPROVE BONUS
  ======================================================= */

  const approveBonus = async (
    item
  ) => {
    if (updatingId) {
      return;
    }

    if (
      item.isWeeklyDeposit &&
      item.selfBonusGiven ===
        true
    ) {
      alert(
        "Weekly Bonus hii tayari imeclaimiwa na user."
      );

      return;
    }

    if (
      item.selfBonusGiven ===
      true
    ) {
      alert(
        "Bonus hii tayari imelipwa."
      );

      return;
    }

    if (
      item.adminBonusGiven ===
      true
    ) {
      alert(
        "Bonus hii tayari imelipwa na Admin."
      );

      return;
    }

    const uniqueId =
      item.uniqueId;

    setUpdatingId(
      uniqueId
    );

    try {
      /*
        IMPORTANT:

        This automatically selects:

        tsh_transactions
        usdt_transactions
        visterdeposte
      */

      const transactionRef =
        doc(
          db,
          item.bonusCollection,
          item.id
        );

      let paidUserAmount = 0;

      let paidReferralAmount = 0;

      let paidReferralUsername =
        "";

      let paidUserCurrency =
        item.bonusCurrency ||
        item.displayCurrency ||
        "TZS";

      let paidReferralCurrency =
        "";

      let paidUserBalanceField =
        "";

      let paidReferralBalanceField =
        "";

      let referralWasPaid =
        false;

      let paidReferralBonusTZS =
        0;

      await runTransaction(
        db,
        async (transaction) => {
          /* =================================================
             DEPOSIT
          ================================================= */

          const depositSnapshot =
            await transaction.get(
              transactionRef
            );

          if (
            !depositSnapshot.exists()
          ) {
            throw new Error(
              "Deposit not found."
            );
          }

          const fresh =
            depositSnapshot.data();

          if (
            normalizeStatus(
              fresh.status
            ) !== "success"
          ) {
            throw new Error(
              "Deposit is not successful."
            );
          }

          if (
            fresh.selfBonusGiven ===
            true
          ) {
            throw new Error(
              "Bonus tayari imelipwa kwa user."
            );
          }

          if (
            fresh.adminBonusGiven ===
            true
          ) {
            throw new Error(
              "Bonus hii tayari imelipwa na Admin."
            );
          }

          const depositTimestamp =
            getTimestampSeconds(
              fresh
            );

          if (
            depositTimestamp <= 0
          ) {
            throw new Error(
              "Deposit timestamp haijapatikana."
            );
          }

          const username =
            fresh.username ||
            item.username;

          if (!username) {
            throw new Error(
              "Username ya deposit haijapatikana."
            );
          }

          /* =================================================
             USER
          ================================================= */

          const userDoc =
            await findUserByUsername(
              username
            );

          if (!userDoc) {
            throw new Error(
              `User "${username}" hajapatikana.`
            );
          }

          const userSnapshot =
            await transaction.get(
              userDoc.ref
            );

          if (
            !userSnapshot.exists()
          ) {
            throw new Error(
              "User document haipo."
            );
          }

          const userData =
            userSnapshot.data();

          /* =================================================
             USER CURRENCY
          ================================================= */

          let userCurrency;

          /*
            Tanzania
          */

          if (
            item.bonusCollection ===
            TSH_COLLECTION
          ) {
            userCurrency =
              TZS_CURRENCY;
          }

          /*
            USDT
          */

          else if (
            item.bonusCollection ===
            USDT_COLLECTION
          ) {
            userCurrency =
              USDT_CURRENCY;
          }

          /*
            Visitor
          */

          else {
            userCurrency =
              getVisitorCurrency({
                ...item,
                ...fresh,
              });
          }

          if (!userCurrency) {
            throw new Error(
              "User currency haijapatikana."
            );
          }

          paidUserCurrency =
            userCurrency;

          
            let balanceField = "";

if (
  item.bonusCollection === USDT_COLLECTION
) {
  // USDT deposit → USDT bonus → usdtBalance
  balanceField = "usdtBalance";
} else if (
  item.bonusCollection === TSH_COLLECTION
) {
  // TZS deposit → TZS bonus → tshBalance
  balanceField = "tshBalance";
} else {
  // Visitor currencies
  balanceField =
    getBalanceFieldFromCurrency(
      userCurrency
    );
}

          if (!balanceField) {
            throw new Error(
              `User balance field haijulikani kwa ${userCurrency}.`
            );
          }

          paidUserBalanceField =
            balanceField;

          /* =================================================
             DEPOSIT AMOUNT
          ================================================= */

          const originalDepositAmount =
            Number(
              fresh.amount ??
                item.originalAmount ??
                item.amount ??
                0
            );

          if (
            originalDepositAmount <=
            0
          ) {
            throw new Error(
              "Deposit amount haijapatikana."
            );
          }

          /* =================================================
             CURRENCY
          ================================================= */

          let transactionCurrency;

          if (
            item.bonusCollection ===
            TSH_COLLECTION
          ) {
            transactionCurrency =
              TZS_CURRENCY;
          } else if (
            item.bonusCollection ===
            USDT_COLLECTION
          ) {
            transactionCurrency =
              USDT_CURRENCY;
          } else {
            transactionCurrency =
              getVisitorCurrency({
                ...item,
                ...fresh,
              });
          }

          /* =================================================
             TZS VALUE
          ================================================= */

          const tzsEquivalent =
            item.bonusCollection ===
              TSH_COLLECTION
              ? originalDepositAmount
              : convertToTZS(
                  originalDepositAmount,
                  transactionCurrency,
                  fresh.country
                );

          if (
            tzsEquivalent <= 0
          ) {
            throw new Error(
              `TZS equivalent haijapatikana kwa ${transactionCurrency}.`
            );
          }

          console.log(
            "BONUS CALCULATION:",
            {
              collection:
                item.bonusCollection,

              originalDepositAmount,

              transactionCurrency,

              tzsEquivalent,

              usdtRate:
                USDT_TO_TZS_RATE,
            }
          );

          /* =================================================
             DEPOSIT TYPE
          ================================================= */

          let depositType =
            normalizeDepositType(
              fresh.depositOrderType ||
                fresh.position ||
                item.depositOrderType
            );

          if (
            depositType !==
              "first" &&
            depositType !==
              "second" &&
            depositType !==
              "other"
          ) {
            depositType =
              item.depositOrderType ||
              "other";
          }

          /* =================================================
             WEEKLY
          ================================================= */

          const weeklyEligible =
            depositType ===
              "other" &&
            isWednesday(
              depositTimestamp
            );

          /* =================================================
             USER BONUS
          ================================================= */

          let bonusData;

          if (
            weeklyEligible
          ) {
            bonusData =
              getWeeklyBonusData(
                originalDepositAmount,
                depositTimestamp
              );
          } else if (
            depositType ===
            "first"
          ) {
            bonusData =
              getFirstDepositBonusData(
                originalDepositAmount,
                tzsEquivalent
              );
          } else if (
            depositType ===
            "second"
          ) {
            bonusData =
              getSecondDepositBonusData(
                originalDepositAmount,
                tzsEquivalent
              );
          } else {
            bonusData = {
              locked: true,
              eligible: false,
              percent: 0,
              amount: 0,
              amountTZS: 0,

              label:
                "No Bonus",

              bonusType:
                "other",
            };
          }

          if (
            bonusData.locked
          ) {
            throw new Error(
              `Bonus hii haijafikia eligibility. TZS value: ${tzsEquivalent.toLocaleString()} TZS.`
            );
          }

          /* =================================================
             CALCULATE USER BONUS
          ================================================= */

          let calculatedBonus =
            0;

          if (
            bonusData.fixedTZS ===
            true
          ) {
            calculatedBonus =
              convertTZSToCurrency(
                500000,
                userCurrency,
                fresh.country
              );
          } else {
            calculatedBonus =
              Number(
                bonusData.amount ||
                  0
              );
          }

          if (
            calculatedBonus <= 0
          ) {
            throw new Error(
              "Hakuna bonus ya kulipwa."
            );
          }

          paidUserAmount =
            calculatedBonus;

          /* =================================================
             USER BALANCE
          ================================================= */

          const currentBalance =
            Number(
              userData[
                balanceField
              ] || 0
            );

          const newBalance =
            currentBalance +
            calculatedBonus;

          /* =================================================
             REFERRAL
          ================================================= */

          let referralUsername =
            String(
              fresh.referralBy ||
                item.referralBy ||
                getUserReferralUsername(
                  userData
                ) ||
                ""
            ).trim();

          if (
            normalizeUsername(
              referralUsername
            ) === "none" ||
            normalizeUsername(
              referralUsername
            ) === "unknown"
          ) {
            referralUsername = "";
          }

          const referralAlreadyPaid =
            isReferralAlreadyPaid(
              fresh
            );

          const referralData =
            getReferralBonusData(
              depositType,
              tzsEquivalent
            );

          let referralBonusTZS =
            Number(
              fresh.referralBonusAmountTZS ??
                item.referralBonusAmountTZS ??
                0
            );

          if (
            referralBonusTZS <= 0
          ) {
            referralBonusTZS =
              Number(
                referralData.amountTZS ||
                  0
              );
          }

          let referralDoc =
            null;

          /*
            Referral only for FIRST deposit.
          */

          if (
            depositType ===
              "first" &&
            referralUsername &&
            referralBonusTZS > 0 &&
            !referralAlreadyPaid
          ) {
            /* =================================================
               SELF REFERRAL
            ================================================= */

            if (
              normalizeUsername(
                referralUsername
              ) ===
              normalizeUsername(
                username
              )
            ) {
              throw new Error(
                "Self referral hairuhusiwi."
              );
            }

            /* =================================================
               FIND REFERRAL USER
            ================================================= */

            referralDoc =
              await findUserByUsername(
                referralUsername
              );

            if (!referralDoc) {
              throw new Error(
                `Referral "${referralUsername}" hajapatikana.`
              );
            }

            const referralSnapshot =
              await transaction.get(
                referralDoc.ref
              );

            if (
              !referralSnapshot.exists()
            ) {
              throw new Error(
                "Referral document haipo."
              );
            }

            const referralUserData =
              referralSnapshot.data();

            /*
              IMPORTANT:

              Referral currency =
              DEPOSIT currency.

              USDT deposit
              → USDT referral

              TZS deposit
              → TZS referral
            */

            const referralCurrency =
              transactionCurrency;

            paidReferralCurrency =
              referralCurrency;

            const referralBalanceField =
              getBalanceFieldFromCurrency(
                referralCurrency
              );

            if (
              !referralBalanceField
            ) {
              throw new Error(
                `Referral balance field haijulikani kwa ${referralCurrency}.`
              );
            }

            paidReferralBalanceField =
              referralBalanceField;

            /*
              TZS referral reward
              → original deposit currency
            */

            const referralPayAmount =
              convertTZSToCurrency(
                referralBonusTZS,
                referralCurrency,
                fresh.country
              );

            if (
              referralPayAmount <= 0
            ) {
              throw new Error(
                `Referral bonus haikuweza kubadilishwa kwenda ${referralCurrency}.`
              );
            }

            const referralCurrentBalance =
              Number(
                referralUserData[
                  referralBalanceField
                ] || 0
              );

            const referralNewBalance =
              referralCurrentBalance +
              referralPayAmount;

            transaction.update(
              referralDoc.ref,
              {
                [referralBalanceField]:
                  referralNewBalance,
              }
            );

            paidReferralAmount =
              referralPayAmount;

            paidReferralUsername =
              referralUsername;

            paidReferralBonusTZS =
              referralBonusTZS;

            referralWasPaid =
              true;
          }

          /* =================================================
             USER BALANCE UPDATE
          ================================================= */

          transaction.update(
            userDoc.ref,
            {
              [balanceField]:
                newBalance,
            }
          );

          /* =================================================
             DEPOSIT UPDATE
          ================================================= */

          const depositUpdate = {
            selfBonusGiven:
              true,

            adminBonusGiven:
              true,

            selfBonusClaimedAmount:
              calculatedBonus,

            selfBonusApprovedBy:
              "admin",

            selfBonusApprovedAt:
              serverTimestamp(),

            selfBonusLocked:
              false,

            selfBonusAmount:
              calculatedBonus,

            selfBonusCurrency:
              userCurrency,

            selfBonusLabel:
              bonusData.label ||
              "Bonus",

            selfBonusPercent:
              Number(
                bonusData.percent ||
                  0
              ),

            selfBonusType:
              weeklyEligible
                ? "weekly"
                : depositType,

            bonusPaymentCurrency:
              userCurrency,

            bonusBalanceField:
              balanceField,

            bonusDepositAmount:
              originalDepositAmount,

            bonusDepositCurrency:
              userCurrency,

            bonusDepositTZSEquivalent:
              tzsEquivalent,

            /*
              WEEKLY
            */

            ...(weeklyEligible
              ? {
                  weeklyBonusWeek:
                    getWednesdayKey(
                      depositTimestamp
                    ),

                  weeklyBonusPercent:
                    WEEKLY_BONUS_PERCENT,

                  weeklyBonusAmount:
                    calculatedBonus,

                  weeklyBonusCurrency:
                    userCurrency,
                }
              : {}),

            /* =================================================
               REFERRAL
            ================================================= */

            referralBonusGiven:
              referralAlreadyPaid ||
              referralWasPaid,

            referralBonusClaimed:
              referralAlreadyPaid ||
              referralWasPaid,

            referralClaimed:
              referralAlreadyPaid ||
              referralWasPaid,

            referralBonusStatus:
              referralAlreadyPaid ||
              referralWasPaid
                ? "paid"
                : fresh.referralBonusStatus ||
                  fresh.referralStatus ||
                  "pending",

            referralBonusReceiver:
              referralWasPaid
                ? paidReferralUsername
                : fresh.referralBonusReceiver ||
                  "",

            referralBonusClaimedAmount:
              referralWasPaid
                ? paidReferralAmount
                : Number(
                    fresh.referralBonusClaimedAmount ||
                      0
                  ),

            referralBonusClaimedAmountTZS:
              referralWasPaid
                ? paidReferralBonusTZS
                : Number(
                    fresh.referralBonusClaimedAmountTZS ||
                      0
                  ),

            referralBonusAmountTZS:
              referralBonusTZS,

            referralBonusPercent:
              Number(
                referralData.percent ||
                  fresh.referralBonusPercent ||
                  0
              ),

            referralBonusLabel:
              referralData.label ||
              fresh.referralBonusLabel ||
              "",

            /*
              SAME CURRENCY
              AS DEPOSIT
            */

            referralBonusCurrency:
              referralWasPaid
                ? paidReferralCurrency
                : fresh.referralBonusCurrency ||
                  "",

            referralBonusBalanceField:
              referralWasPaid
                ? paidReferralBalanceField
                : fresh.referralBonusBalanceField ||
                  "",

            referralBonusClaimedAt:
              referralWasPaid
                ? serverTimestamp()
                : fresh.referralBonusClaimedAt ||
                  fresh.referralClaimedAt ||
                  null,
          };

          /*
            Save everything to the
            original transaction collection.

            For USDT:

            usdt_transactions
          */

          transaction.update(
            transactionRef,
            depositUpdate
          );
        }
      );

      /* =================================================
         UI UPDATE
      ================================================= */

      setBonusList(
        (previous) =>
          previous.map(
            (tx) => {
              if (
                tx.uniqueId !==
                uniqueId
              ) {
                return tx;
              }

              return {
                ...tx,

                selfBonusGiven:
                  true,

                adminBonusGiven:
                  true,

                selfBonusAmount:
                  paidUserAmount,

                selfBonusCurrency:
                  paidUserCurrency,

                referralBonusGiven:
                  tx.referralBonusGiven ||
                  referralWasPaid,

                referralBonusClaimed:
                  tx.referralBonusClaimed ||
                  referralWasPaid,

                referralClaimed:
                  tx.referralClaimed ||
                  referralWasPaid,

                referralBonusStatus:
                  referralWasPaid
                    ? "paid"
                    : tx.referralBonusStatus ||
                      tx.referralStatus ||
                      "",

                referralBonusClaimedAt:
                  referralWasPaid
                    ? new Date()
                    : tx.referralBonusClaimedAt ||
                      null,

                referralBonusReceiver:
                  referralWasPaid
                    ? paidReferralUsername
                    : tx.referralBonusReceiver,

                referralBonusAmount:
                  referralWasPaid
                    ? paidReferralAmount
                    : tx.referralBonusAmount,

                referralBonusAmountTZS:
                  referralWasPaid
                    ? paidReferralBonusTZS
                    : tx.referralBonusAmountTZS,

                referralBonusCurrency:
                  referralWasPaid
                    ? paidReferralCurrency
                    : tx.referralBonusCurrency,

                referralBonusBalanceField:
                  referralWasPaid
                    ? paidReferralBalanceField
                    : tx.referralBonusBalanceField,
              };
            }
          )
      );

      /* =================================================
         ALERT
      ================================================= */

      if (
        referralWasPaid
      ) {
        alert(
          `Bonus imelipwa successfully.\n\n `+
            `User: ${formatMoney(
              paidUserAmount,
              paidUserCurrency
            )}\n` +
            `Referral (${paidReferralUsername}): ${formatMoney(
              paidReferralAmount,
              paidReferralCurrency
            )}`
        );
      } else if (
        isReferralAlreadyPaid(item)
      ) {
        alert(
          `Bonus ya User imelipwa successfully.\n\n `+
            `User: ${formatMoney(
              paidUserAmount,
              paidUserCurrency
            )}\n\n` +
            `Referral tayari alikuwa amelipwa.`
        );
      } else {
        alert(
          `Bonus imelipwa successfully.\n\n `+
            `User: ${formatMoney(
              paidUserAmount,
              paidUserCurrency
            )}\n\n` +
            `Referral haikulipwa.`
        );
      }
    } catch (error) {
      console.error(
        "Bonus approval failed:",
        error
      );

      alert(
        error?.message ||
          "Bonus approval failed."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredBonusList =
    bonusList.filter(
      (item) => {
        const search =
          searchUser
            .toLowerCase()
            .trim();

        if (!search) {
          return true;
        }

        return (
          String(
            item.username || ""
          )
            .toLowerCase()
            .includes(search) ||

          String(
            item.depositOrderType ||
              ""
          )
            .toLowerCase()
            .includes(search) ||

          String(
            item.displayCurrency ||
              ""
          )
            .toLowerCase()
            .includes(search) ||

          String(
            item.referralBy || ""
          )
            .toLowerCase()
            .includes(search) ||

          String(
            item.coin || ""
          )
            .toLowerCase()
            .includes(search)
        );
      }
    );

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="admin-bonus-dashboard">
    
      {/* =================================================
         HEADER
      ================================================= */}

      <div className="admin-bonus-header">
        <div>
          <h2>
            Digital Bonus Dashboard
          </h2>

          <p>
            Tanzania, USDT, Visitor &
            Weekly Deposit Bonuses
          </p>
        </div>

        <button
          type="button"
          className="admin-bonus-refresh-btn"
          onClick={
            fetchBonuses
          }
          disabled={loading}
        >
          {loading
            ? "Loading..."
            : "Refresh"}
        </button>
      </div>

      {/* =================================================
         USDT RATE
      ================================================= */}

      <div
        style={{
          padding:
            "10px 14px",
          marginBottom:
            "12px",
          borderRadius:
            "8px",
          fontSize:
            "13px",
          fontWeight:
            "600",
        }}
      >
        1 USDT ={" "}
        {USDT_TO_TZS_RATE.toLocaleString()}{" "}
        TZS
      </div>

      {/* =================================================
         SEARCH
      ================================================= */}

      <div className="bonus-search-box">
        <input
          type="text"
          placeholder="Tafuta username..."
          value={searchUser}
          onChange={(e) =>
            setSearchUser(
              e.target.value
            )
          }
        />
      </div>

      {/* =================================================
         LOADING
      ================================================= */}

      {loading ? (
        <div className="admin-bonus-loading">
          <div className="admin-bonus-spinner" />

          <span>
            Inapakia bonuses...
          </span>
        </div>
      ) : (
        <div className="bonus-table-wrapper">
          <table className="bonus-table">
            <thead>
              <tr>
                <th>Date</th>

                <th>User</th>

                <th>Source</th>

                <th>Deposit</th>

                <th>Currency</th>

                <th>TZS Value</th>

                <th>Type</th>

                <th>User Bonus</th>

                <th>User Status</th>

                <th>Referral</th>

                <th>Referral Bonus</th>

                <th>Referral Status</th>

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredBonusList.length ===
              0 ? (
                <tr>
                  <td
                    colSpan="13"
                    className="bonus-no-records"
                  >
                    No bonus records
                    found.
                  </td>
                </tr>
              ) : (
                filteredBonusList.map(
                  (item) => {
                    /* =================================================
                       STATUS
                    ================================================= */

                    const userPaid =
                      item.selfBonusGiven ===
                      true;

                    const adminPaid =
                      item.adminBonusGiven ===
                      true;

                    const referralPaid =
                      isReferralAlreadyPaid(
                        item
                      );

                    const isUpdating =
                      updatingId ===
                      item.uniqueId;

                    const weeklyUserClaimed =
                      item.isWeeklyDeposit &&
                      item.selfBonusGiven ===
                        true;

                    /* =================================================
                       CURRENCY
                    ================================================= */

                    const itemCurrency =
                      item.bonusCurrency ||
                      item.displayCurrency ||
                      "TZS";

                    /* =================================================
                       AMOUNT
                    ================================================= */

                    const itemOriginalAmount =
                      Number(
                        item.originalAmount ||
                          item.amount ||
                          0
                      );

                    const itemTZSEquivalent =
                      Number(
                        item.tshEquivalent ||
                          item.displayTZSValue ||
                          0
                      );

                    /* =================================================
                       FRESH BONUS DATA
                    ================================================= */

                    let freshBonusData;

                    if (
                      item.isWeeklyDeposit
                    ) {
                      freshBonusData =
                        getWeeklyBonusData(
                          itemOriginalAmount,
                          item.timestamp
                        );
                    } else if (
                      item.depositOrderType ===
                      "first"
                    ) {
                      freshBonusData =
                        getFirstDepositBonusData(
                          itemOriginalAmount,
                          itemTZSEquivalent
                        );
                    } else if (
                      item.depositOrderType ===
                      "second"
                    ) {
                      freshBonusData =
                        getSecondDepositBonusData(
                          itemOriginalAmount,
                          itemTZSEquivalent
                        );
                    } else {
                      freshBonusData = {
                        locked: true,

                        amount: 0,

                        amountTZS: 0,

                        label:
                          "No Bonus",
                      };
                    }

                    const actuallyLocked =
                      freshBonusData.locked ===
                      true;

                    /* =================================================
                       CURRENT BONUS
                    ================================================= */

                    let currentNativeBonus =
                      0;

                    if (
                      !actuallyLocked
                    ) {
                      if (
                        freshBonusData.fixedTZS ===
                        true
                      ) {
                        currentNativeBonus =
                          convertTZSToCurrency(
                            500000,
                            itemCurrency,
                            item.country
                          );
                      } else {
                        currentNativeBonus =
                          Number(
                            freshBonusData.amount ||
                              0
                          );
                      }
                    }

                    const displayUserBonus =
                      userPaid
                        ? Number(
                            item.selfBonusAmount ||
                              item.selfBonusClaimedAmount ||
                              0
                          )
                        : currentNativeBonus;

                    /* =================================================
                       REFERRAL
                    ================================================= */

                    const calculatedReferralData =
                      getReferralBonusData(
                        item.depositOrderType,
                        itemTZSEquivalent
                      );

                    const calculatedReferralTZS =
                      Number(
                        calculatedReferralData.amountTZS ||
                          0
                      );

                    const displayReferralBonus =
                      Number(
                        item.referralBonusAmount ||
                          item.referralBonusClaimedAmount ||
                          0
                      );

                    const referralPendingTZS =
                      Number(
                        item.referralBonusAmountTZS ||
                          calculatedReferralTZS ||
                          0
                      );

                    /* =================================================
                       BUTTON
                    ================================================= */

                    const disabled =
                      isUpdating ||
                      userPaid ||
                      adminPaid ||
                      actuallyLocked ||
                      displayUserBonus <=
                        0;

                    /* =================================================
                       SOURCE
                    ================================================= */

                    let sourceLabel;

                    if (
                      item.bonusCollection ===
                      USDT_COLLECTION
                    ) {
                      sourceLabel =
                        "USDT";
                    } else if (
                      item.bonusCollection ===
                      VISITOR_COLLECTION
                    ) {
                      sourceLabel =
                        "Visitor";
                    } else {
                      sourceLabel =
                        "Tanzania";
                    }

                    /* =================================================
                       TYPE
                    ================================================= */

                    let typeLabel =
                      item.depositOrderType;

                    if (
                      item.isWeeklyDeposit
                    ) {
                      typeLabel =
                        "Weekly";
                    }

                    return (
                      <tr
                        key={
                          item.uniqueId
                        }
                      >
                        {/* DATE */}

                        <td>
                          {item.timestamp >
                          0
                            ? new Date(
                                item.timestamp *
                                  1000
                              ).toLocaleString()
                            : "-"}
                        </td>

                        {/* USER */}

                        <td className="bonus-username">
                          {item.username ||
                            "Unknown"}
                        </td>

                        {/* SOURCE */}

                        <td>
                          <span
                            className={
                              item.bonusCollection ===
                              USDT_COLLECTION
                                ? "deposit-type deposit-visitor"
                                : item.bonusCollection ===
                                  VISITOR_COLLECTION
                                ? "deposit-type deposit-visitor"
                                : "deposit-type deposit-tanzania"
                            }
                          >
                            {
                              sourceLabel
                            }
                          </span>
                        </td>

                        {/* DEPOSIT */}

                        <td>
                          {formatMoney(
                            item.displayAmount,
                            item.displayCurrency
                          )}
                        </td>

                        {/* CURRENCY */}

                        <td>
                          {item.displayCurrency ||
                            item.coin ||
                            "-"}
                        </td>

                        {/* TZS VALUE */}

                        <td>
                          {item.displayTZSValue
                            ? formatMoney(
                                item.displayTZSValue,
                                "TZS"
                              )
                            : "-"}

                          <div
                            style={{
                              fontSize:
                                "9px",

                              opacity:
                                0.55,

                              marginTop:
                                "3px",
                            }}
                          >
                            Qualification
                            Value
                          </div>
                        </td>

                        {/* TYPE */}

                        <td>
                          <span className="deposit-type">
                            {typeLabel}
                          </span>

                          {item.isWeeklyDeposit && (
                            <div
                              style={{
                                fontSize:
                                  "9px",

                                opacity:
                                  0.6,

                                marginTop:
                                  "4px",
                              }}
                            >
                              Wednesday
                              5%
                            </div>
                          )}
                        </td>

                        {/* USER BONUS */}

                        <td>
                          <strong>
                            {formatMoney(
                              displayUserBonus,
                              itemCurrency
                            )}
                          </strong>

                          <div
                            style={{
                              fontSize:
                                "10px",

                              opacity:
                                0.65,

                              marginTop:
                                "4px",
                            }}
                          >
                            {userPaid
                              ? item.selfBonusLabel
                              : freshBonusData.label}
                          </div>

                          {item.isWeeklyDeposit && (
                            <div
                              style={{
                                fontSize:
                                  "9px",

                                opacity:
                                  0.55,

                                marginTop:
                                  "3px",
                              }}
                            >
                              Original
                              Currency
                              × 5%
                            </div>
                          )}
                        </td>

                        {/* USER STATUS */}

                        <td>
                          <span
                            className={
                              weeklyUserClaimed
                                ? "bonus-paid"
                                : userPaid
                                ? "bonus-paid"
                                : actuallyLocked
                                ? "bonus-locked"
                                : "bonus-pending"
                            }
                          >
                            {weeklyUserClaimed
                              ? "User Claimed"
                              : userPaid
                              ? "Paid"
                              : actuallyLocked
                              ? "Locked"
                              : "Pending"}
                          </span>
                        </td>

                        {/* REFERRAL */}

                        <td>
                          {item.referralBy &&
                          item.referralBy !==
                            "None"
                            ? item.referralBy
                            : "No referral"}

                          {item.depositOrderType ===
                            "first" &&
                            item.referralBy &&
                            item.referralBy !==
                              "None" && (
                              <div
                                style={{
                                  fontSize:
                                    "9px",

                                  opacity:
                                    0.6,

                                  marginTop:
                                    "4px",
                                }}
                              >
                                Referral
                                eligible
                              </div>
                            )}
                        </td>

                        {/* REFERRAL BONUS */}

                        <td>
                          <strong>
                            {displayReferralBonus >
                            0
                              ? formatMoney(
                                  displayReferralBonus,
                                  item.referralBonusCurrency ||
                                    item.bonusCurrency ||
                                    item.displayCurrency
                                )
                              : referralPendingTZS >
                                0
                              ? formatMoney(
                                  convertTZSToCurrency(
                                    referralPendingTZS,
                                    item.displayCurrency ||
                                      item.bonusCurrency ||
                                      "TZS",
                                    item.country
                                  ),
                                  item.displayCurrency ||
                                    item.bonusCurrency ||
                                    "TZS"
                                )
                              : "0"}
                          </strong>

                          {item.referralBonusCurrency && (
                            <div
                              style={{
                                fontSize:
                                  "9px",

                                opacity:
                                  0.6,

                                marginTop:
                                  "3px",
                              }}
                            >
                              {
                                item.referralBonusCurrency
                              }
                            </div>
                          )}

                          {!referralPaid &&
                            item.depositOrderType ===
                              "first" &&
                            referralPendingTZS >
                              0 && (
                              <div
                                style={{
                                  fontSize:
                                    "9px",

                                  opacity:
                                    0.6,

                                  marginTop:
                                    "3px",
                                }}
                              >
                                Pending
                              </div>
                            )}

                          {referralPaid && (
                            <div
                              style={{
                                fontSize:
                                  "9px",

                                opacity:
                                  0.6,

                                marginTop:
                                  "3px",
                              }}
                            >
                              Referral
                              already
                              claimed
                            </div>
                          )}
                        </td>

                        {/* REFERRAL STATUS */}

                        <td>
                          <span
                            className={
                              referralPaid
                                ? "bonus-paid"
                                : referralPendingTZS >
                                  0
                                ? "bonus-pending"
                                : "bonus-locked"
                            }
                          >
                            {referralPaid
                              ? "Paid"
                              : referralPendingTZS >
                                0
                              ? "Pending"
                              : "No referral"}
                          </span>
                        </td>

                        {/* ACTION */}

                        <td>
                          {weeklyUserClaimed ? (
                            <button
                              type="button"
                              className="approve-btn"
                              disabled
                            >
                              User Claimed
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="approve-btn"
                              disabled={
                                disabled
                              }
                              onClick={() =>
                                approveBonus(
                                  item
                                )
                              }
                            >
                              {isUpdating
                                ? "Paying..."
                                : userPaid
                                ? "Already Paid"
                                : adminPaid
                                ? "Already Paid"
                                : actuallyLocked
                                ? "Locked"
                                : displayUserBonus <=
                                  0
                                ? "No Bonus"
                                : "Approve Bonus"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}